import React, {useEffect, useId, useRef, useState} from 'react';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {ReactComponent as CaretDownIcon} from '../../../../assets/images/caret-down.svg';
import {ReactComponent as CaretUpIcon} from '../../../../assets/images/caret-up.svg';
import Search from '@mui/icons-material/Search';
import Cancel from '@mui/icons-material/Cancel';
import {AIReviewJobStatus} from './utils';
import {useNavigate} from 'react-router-dom';
import {useGetAIReviewJobDetails} from '../../../../hooks/ai/review/useReview';
import {useDispatch} from 'react-redux';
import {JobType} from '../../../../api/types/aiReview.type';
import {JobDetailFormData} from '../create-job/new-job-detail/NewJobDetail';
import {SourceSelectionMode} from '../create-job/new-job-detail/source-selection-step/SourceSelectionStep';
import {
  setCloneJobName,
  setFormData,
  setJobType,
} from '../../../../redux/slice/aiReviewSlice';

type MenuHelpers = {
  close: () => void;
};

type GroupOption = {
  id: string;
  group_name: string;
};

interface JobListActionMenuItemProps {
  item: {id: string};
  helpers: MenuHelpers;
  selectedRowStatus: AIReviewJobStatus | null;
  selectedRowGroupName: string | null;
  selectedMenuGroupName: string | null;
  selectedRowJobName: string | null;
  selectedRowJobId: string | number | null;
  isAddToGroupExpanded: boolean;
  groupSearchValue: string;
  groupSearchPerformed: boolean;
  visibleGroups: GroupOption[];
  onToggleAddToGroup: () => void;
  onGroupSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onGroupSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onGroupSearchClear: () => void;
  onAddToGroup: (groupId: string, groupName: string) => void;
  onRemoveFromGroup: (groupName: string | null) => void;
  onOpenDeleteJob: (jobName: string, jobId: string | number | null) => void;
  onRetryJob: (jobId: string | number | null, jobName: string | null) => void;
  onExportJobDetailsAsPdf: (jobId: string | number | null) => Promise<void>;
}

const JobListActionMenuItem = ({
  item,
  helpers,
  selectedRowStatus,
  selectedRowGroupName,
  selectedMenuGroupName,
  selectedRowJobName,
  selectedRowJobId,
  isAddToGroupExpanded,
  groupSearchValue,
  groupSearchPerformed,
  visibleGroups,
  onToggleAddToGroup,
  onGroupSearchChange,
  onGroupSearchKeyDown,
  onGroupSearchClear,
  onAddToGroup,
  onRemoveFromGroup,
  onOpenDeleteJob,
  onRetryJob,
  onExportJobDetailsAsPdf,
}: JobListActionMenuItemProps) => {
  const addToGroupHeadingId = useId();
  const addToGroupSubmenuId = useId();
  const addToGroupMenuItemRef = useRef<HTMLLIElement | null>(null);
  const wasAddToGroupExpandedRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [shouldNavigate, setShouldNavigate] = useState(false);

  useEffect(() => {
    if (item.id !== 'addToGroup') return;

    const wasExpanded = wasAddToGroupExpandedRef.current;
    const nowExpanded = isAddToGroupExpanded;

    if (wasExpanded && !nowExpanded) {
      const rafId = requestAnimationFrame(() => {
        addToGroupMenuItemRef.current?.focus({preventScroll: true});
      });
      wasAddToGroupExpandedRef.current = nowExpanded;
      return () => cancelAnimationFrame(rafId);
    }

    wasAddToGroupExpandedRef.current = nowExpanded;
  }, [item.id, isAddToGroupExpanded]);

  if (!selectedRowStatus) return null;

  const isInProgress = selectedRowStatus === AIReviewJobStatus.InProgress;
  const isFailed = selectedRowStatus === AIReviewJobStatus.Failed;
  const isDraft = selectedRowStatus === AIReviewJobStatus.Draft;
  const isComplete = selectedRowStatus === AIReviewJobStatus.Complete;

  const {data: getJobDetailData} = useGetAIReviewJobDetails(
    {jobId: selectedRowJobId!},
    shouldNavigate && !!selectedRowJobId
  );

  useEffect(() => {
    if (!getJobDetailData || !selectedRowJobId || !shouldNavigate) return;

    const draftJobType: JobType = getJobDetailData.jobType;
    const draftFormData: JobDetailFormData = {
      jobDetail: {
        jobName: '',
        description: getJobDetailData.description,
        selectedGroup: {
          id: getJobDetailData.groupID,
        },
      },
      contextAndCriteria: {
        context: getJobDetailData.caseContext,
        issueCodes: getJobDetailData.issueCodes,
        relevanceCriteria: getJobDetailData.relevanceCriteria,
      },
      sourceSelection: 0,
      sourceSelectionMode: 'SAVEDSEARCH' as SourceSelectionMode,
      groundTruth: getJobDetailData.groundTruth ?? '',
    };

    dispatch(setFormData(draftFormData));
    dispatch(setJobType(draftJobType));
    dispatch(setCloneJobName(getJobDetailData.jobName));
    navigate('/AI/Review/CreateJob');
    setShouldNavigate(false);
    helpers.close();
  }, [getJobDetailData, selectedRowJobId, shouldNavigate]);
  const handleCloneJob = () => {
    setShouldNavigate(true);
  };

  if (
    item.id === 'divider' ||
    item.id === 'divider-1' ||
    item.id === 'divider-2'
  ) {
    return (
      <Divider
        className="job-list__actions-menu-divider"
        role="separator"
        aria-orientation="horizontal"
      />
    );
  }

  if (item.id === 'addToGroup') {
    const groupNameToShow = selectedMenuGroupName ?? selectedRowGroupName;
    const addToGroupLabel = groupNameToShow
      ? `${t('ai.review.jobList.jobListActions.group')} ${groupNameToShow}`
      : t('ai.review.jobList.jobListActions.addToGroup');

    return (
      <MenuItem
        ref={addToGroupMenuItemRef}
        aria-expanded={isAddToGroupExpanded}
        aria-haspopup="menu"
        aria-controls={isAddToGroupExpanded ? addToGroupSubmenuId : undefined}
        className={`job-list__add-to-group-wrapper job-list__actions-menu-item${
          isAddToGroupExpanded
            ? ' job-list__add-to-group-wrapper--expanded'
            : ''
        }`}
        disableRipple
        onClick={onToggleAddToGroup}
        data-testid={`job-list-action-menu-${groupNameToShow}`}
      >
        <Box className="job-list__add-to-group-row">
          <ListItemText
            primary={addToGroupLabel}
            className="job-list__actions-menu-label typo-text body2_size"
            slotProps={{
              primary: {id: addToGroupHeadingId, component: 'span'},
            }}
          />
          <Box
            className="job-list__actions-menu-caret"
            data-testid="add-to-group-caret"
            aria-hidden
            component="span"
          >
            {isAddToGroupExpanded ? <CaretUpIcon /> : <CaretDownIcon />}
          </Box>
        </Box>
        {isAddToGroupExpanded && (
          <Box
            className="job-list__group-dropdown"
            data-testid="job-list-group-dropdown"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ArrTextField
              value={groupSearchValue}
              onChange={onGroupSearchChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                e.stopPropagation();
                onGroupSearchKeyDown(e);
              }}
              sizeVariant="small"
              dataTestId="group-search-input"
              placeholder={t('ai.review.jobList.jobListActions.search')}
              autoFocus
              slotProps={{
                htmlInput: {
                  className: 'typo-text body3_size',
                  'aria-label': t('ai.review.jobList.jobListActions.search'),
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {groupSearchValue && groupSearchPerformed ? (
                        <IconButton
                          onClick={onGroupSearchClear}
                          aria-label={t('common.clear')}
                          edge="end"
                          size="small"
                          data-testid="group-search-clear-button"
                          tabIndex={-1}
                        >
                          <Cancel className="clear-btn" fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          aria-label={t('common.search')}
                          edge="end"
                          size="small"
                          disabled={!groupSearchValue.trim()}
                          data-testid="group-search-button"
                          tabIndex={-1}
                        >
                          <Search
                            className="arr-textfield-icon-color"
                            fontSize="small"
                          />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box
              id={addToGroupSubmenuId}
              className="job-list__group-dropdown-list"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby={addToGroupHeadingId}
            >
              {visibleGroups.length === 0 ? (
                <Box
                  role="menuitem"
                  aria-disabled="true"
                  aria-label={t('common.noResults')}
                  className="job-list__group-dropdown-item job-list__group-dropdown-item--empty"
                >
                  <Typography
                    className="typo-text body2_size"
                    aria-hidden
                    data-testid={`job-list-action-menu-item-no-results-${groupNameToShow}`}
                  >
                    {t('common.noResults')}
                  </Typography>
                </Box>
              ) : (
                visibleGroups.map((g, index) => {
                  const isCurrentGroup =
                    (selectedMenuGroupName ?? selectedRowGroupName) ===
                    g.group_name;
                  return (
                    <Box
                      key={g.id}
                      role="menuitem"
                      tabIndex={0}
                      aria-label={g.group_name}
                      aria-posinset={index + 1}
                      aria-setsize={visibleGroups.length}
                      aria-current={isCurrentGroup ? 'true' : undefined}
                      className="job-list__group-dropdown-item"
                      data-testid={`job-list-group-option-${g.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToGroup(g.id, g.group_name);
                        helpers.close();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          onAddToGroup(g.id, g.group_name);
                          helpers.close();
                        }
                      }}
                    >
                      <Typography
                        className="typo-text body2_size"
                        aria-hidden
                        data-testid={`job-list-action-menu-item--${index}`}
                      >
                        {g.group_name}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </MenuItem>
    );
  }

  if (item.id === 'removeFromGroup') {
    const isDisabled = (selectedMenuGroupName ?? selectedRowGroupName) == null;
    return (
      <MenuItem
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) {
            onRemoveFromGroup(selectedMenuGroupName ?? selectedRowGroupName);
            helpers.close();
          }
        }}
        className="job-list__actions-menu-item"
        data-testid={`menu-item-remove-fromGroup`}
        disableRipple
      >
        <ListItemText
          primary={t('ai.review.jobList.jobListActions.removeFromGroup')}
          className="typo-text body2_size"
        />
      </MenuItem>
    );
  }

  if (item.id === 'deleteJob') {
    const isDisabled = isInProgress;
    return (
      <MenuItem
        disabled={isDisabled}
        data-testid={`menu-item-delete-job`}
        onClick={() => {
          if (!isDisabled) {
            onOpenDeleteJob(selectedRowJobName ?? '', selectedRowJobId);
            helpers.close();
          }
        }}
        className={`job-list__actions-menu-item ${
          isFailed || isDraft || isComplete
            ? 'job-list__actions-menu-item--danger'
            : ''
        }`}
        disableRipple
      >
        <ListItemText
          primary={t('ai.review.jobList.jobListActions.deleteJob')}
          className="typo-text body2_size"
        />
      </MenuItem>
    );
  }

  if (isDraft && item.id === 'edit') {
    return (
      <MenuItem
        onClick={() => {
          helpers.close();
          navigate(`/AI/Review/CreateJob?jobId=${selectedRowJobId}`);
        }}
        className="job-list__actions-menu-item"
        data-testid={`menu-item-edit`}
        disableRipple
      >
        <ListItemText
          primary={t('ai.review.jobList.jobListActions.edit')}
          className="typo-text body2_size"
        />
      </MenuItem>
    );
  }

  if (isFailed && item.id === 'retry') {
    return (
      <MenuItem
        onClick={() => {
          onRetryJob(selectedRowJobId, selectedRowJobName);
          helpers.close();
        }}
        className="job-list__actions-menu-item"
        data-testid={`menu-item-retry`}
        disableRipple
      >
        <ListItemText
          primary={t('ai.review.jobList.jobListActions.retry')}
          className="typo-text body2_size"
        />
      </MenuItem>
    );
  }

  if ((isFailed || isDraft || isComplete) && item.id === 'cloneJob') {
    return (
      <MenuItem
        onClick={handleCloneJob}
        className="job-list__actions-menu-item"
        data-testid={`menu-item-clone`}
        disableRipple
      >
        <ListItemText
          primary={t('ai.review.jobList.jobListActions.cloneJob')}
          className="typo-text body2_size"
        />
      </MenuItem>
    );
  }

  if (
    (isFailed || isDraft || isComplete) &&
    item.id === 'exportJobDetailsAsPdf'
  ) {
    return (
      <MenuItem
        onClick={() => {
          onExportJobDetailsAsPdf(selectedRowJobId);
          helpers.close();
        }}
        className="job-list__actions-menu-item"
        data-testid={`menu-item-export`}
        disableRipple
      >
        <ListItemText
          primary={t('ai.review.jobList.jobListActions.exportJobDetailsAsPdf')}
          className="typo-text body2_size"
        />
      </MenuItem>
    );
  }

  return null;
};

export default JobListActionMenuItem;
